import { useCallback, useContext, useMemo } from 'react';

import {
  PromoType
} from '~/domains/promotions/types';
import type {
  BonusDefinition,
  PublicBoost,
  VoucherResponseDto,
} from '~/domains/promotions/types';

import { PromotionsModalsContext } from '../Modals/PromotionsModalsContext';
import { reportPromotionViewTerms } from '../utils/analytics';
import type { PromotionActionLocation } from '../constants/analytics-enums';
import { usePromotionsContext } from '../context/PromotionsContext';
import { normalizeBonus } from '../utils/formatters';

type UseOpenPromoTermsModalParams = {
  location: PromotionActionLocation;
  selectedPromoId?: string | null;
};

export function useOpenPromoTermsModal({
  location,
  selectedPromoId,
}: UseOpenPromoTermsModalParams) {
  const { openModalUnique } = useContext(PromotionsModalsContext);
  const { selectBoost, selectVoucher, selectDepositBonus } = usePromotionsContext();

  const handleBoostApply = useCallback(
    (boost: PublicBoost) => selectBoost(boost, location),
    [selectBoost],
  );
  const handleVoucherApply = useCallback(
    (voucher: VoucherResponseDto) => selectVoucher(voucher, location),
    [selectVoucher],
  );
  const handleDepositBonusAction = useCallback(
    (bonus: BonusDefinition) => selectDepositBonus(bonus, location),
    [selectDepositBonus],
  );

  const viewBoostTerms = useCallback(
    (boost: PublicBoost, onApplyOverride?: () => void) => {
      reportPromotionViewTerms({ location, promotion: boost });
      openModalUnique({
        type: 'PromoTermsModal',
        mode: PromoType.BOOST,
        boost,
        onApply: onApplyOverride ?? handleBoostApply,
      });
    },
    [openModalUnique, location, selectedPromoId],
  );

  const viewVoucherDetails = useCallback(
    (voucher: VoucherResponseDto, onApplyOverride?: () => void) => {
      reportPromotionViewTerms({ location, promotion: voucher });
      openModalUnique({
        type: 'PromoTermsModal',
        mode: PromoType.VOUCHER,
        voucher,
        onApply: onApplyOverride ?? handleVoucherApply,
      });
    },
    [openModalUnique, location, selectedPromoId],
  );

  const viewDepositBonusDetails = useCallback(
    (bonus: BonusDefinition, onApplyOverride?: () => void) => {
      reportPromotionViewTerms({ location, promotion: normalizeBonus(bonus) });
      openModalUnique({
        type: 'PromoTermsModal',
        mode: PromoType.DEPOSIT_BONUS,
        bonus,
        onApply: onApplyOverride ?? handleDepositBonusAction,
      });
    },
    [openModalUnique, location],
  );

  return useMemo(
    () => ({ viewBoostTerms, viewVoucherDetails, viewDepositBonusDetails }),
    [viewBoostTerms, viewVoucherDetails, viewDepositBonusDetails],
  );
}
